import type { Hex } from "@/color/oklch";
import { nearestXterm } from "@/color/xterm256";
import { type TokenDef, tokensOf, weechatOption } from "@/model/tokens";
import { type ExportContext, type Exporter, GENERATED_BY } from "./types";

/** WeeChat's names for terminal colors 0..15. */
export const WEECHAT_ANSI_NAMES = [
  "black",
  "red",
  "green",
  "brown",
  "blue",
  "magenta",
  "cyan",
  "gray",
  "darkgray",
  "lightred",
  "lightgreen",
  "yellow",
  "lightblue",
  "lightmagenta",
  "lightcyan",
  "white",
];

export interface WeechatColor {
  value: string;
  /** True when no terminal color matched and the nearest xterm-256 color was used. */
  approximate: boolean;
  /** Perceptual distance of the approximation (≈1 is barely visible). */
  distance: number;
}

/**
 * Maps a hex color onto something WeeChat can draw (it has no truecolor support):
 * `default` for the terminal's own fg/bg, a named ANSI color when one of the 16 matches
 * exactly (so it follows the Ghostty palette), otherwise the nearest xterm-256 index.
 */
export function weechatColor(ctx: ExportContext, hex: Hex, role: TokenDef["role"]): WeechatColor {
  const exact = (value: string): WeechatColor => ({ value, approximate: false, distance: 0 });
  if (role === "bg" && hex === ctx.color("terminal.background")) return exact("default");
  if (role === "fg" && hex === ctx.color("terminal.foreground")) return exact("default");
  for (let i = 0; i < 16; i++) {
    if (ctx.color(`terminal.ansi${i}`) === hex) return exact(WEECHAT_ANSI_NAMES[i] ?? String(i));
  }
  const near = nearestXterm(hex);
  return { value: String(near.index), approximate: true, distance: near.distance };
}

export interface WeechatSetting {
  option: string;
  value: string;
  approximate: string[];
}

export function weechatSettings(ctx: ExportContext): WeechatSetting[] {
  const tokens = tokensOf("weechat");
  const settings: WeechatSetting[] = [];
  const approx = (t: TokenDef, c: WeechatColor) =>
    c.approximate ? [`${t.label}: ${ctx.color(t.id)} → ${c.value}`] : [];

  const nicks = tokens.filter((t) => t.key.startsWith("nick_color_"));
  const nickColors = nicks.map((t) => ({ t, c: weechatColor(ctx, ctx.color(t.id), "fg") }));
  for (const t of tokens) {
    if (t.key.startsWith("nick_color_") || t.key.startsWith("buflist:")) continue;
    const c = weechatColor(ctx, ctx.color(t.id), t.role);
    settings.push({ option: weechatOption(t.key), value: c.value, approximate: approx(t, c) });
  }
  settings.push({
    option: "weechat.color.chat_nick_colors",
    value: `"${nickColors.map((n) => n.c.value).join(",")}"`,
    approximate: nickColors.flatMap((n) => approx(n.t, n.c)),
  });

  const buflist = (key: string) => {
    const t = tokens.find((x) => x.key === `buflist:${key}`);
    if (!t) throw new Error(`missing buflist token ${key}`);
    return { t, c: weechatColor(ctx, ctx.color(t.id), t.role) };
  };
  const current = buflist("current_bg");
  settings.push({
    option: "buflist.format.buffer_current",
    value: `"\${color:,${current.c.value}}\${format_buffer}"`,
    approximate: approx(current.t, current.c),
  });
  for (const level of ["low", "message", "private", "highlight"] as const) {
    const h = buflist(`hotlist_${level}`);
    settings.push({
      option: `buflist.format.hotlist_${level}`,
      value: `"\${color:${h.c.value}}"`,
      approximate: approx(h.t, h.c),
    });
  }
  return settings;
}

export const weechat: Exporter = {
  id: "weechat",
  label: "WeeChat",
  app: true,
  description: "/set commands for chat, bars, buflist, nick and IRC colors.",
  run: (ctx) => {
    const settings = weechatSettings(ctx);
    const approx = settings.flatMap((s) => s.approximate);
    const lines = [
      `# ${ctx.appName}: "${ctx.theme.name}" (${ctx.theme.variant}), ${GENERATED_BY}`,
      "# Named colors (red, lightblue, …) are the terminal's ANSI colors, so they follow the",
      "# matching Ghostty theme exactly. Numbers are xterm-256 approximations.",
      ...settings.map((s) => `/set ${s.option} ${s.value}`),
      "/save",
    ];
    return {
      files: [
        {
          path: `~/.config/weechat/${ctx.appName}.weechat`,
          filename: `${ctx.appName}.weechat`,
          language: "text",
          content: `${lines.join("\n")}\n`,
        },
      ],
      steps: [
        "Apply the matching Ghostty theme first: WeeChat's named colors come from the terminal palette.",
        `Save the commands below as \`~/.config/weechat/${ctx.appName}.weechat\`.`,
        "Run every `/set …` line inside WeeChat, then `/save`: paste them into the input bar, or send them through WeeChat's FIFO pipe (`/fifo` prints its path and enables it): `sed -n 's|^/|*/|p' FILE > FIFO_PATH`.",
        "Do not edit `weechat.conf` by hand while WeeChat is running: it overwrites the file on `/save` and on exit.",
      ],
      verify: ["In WeeChat: `/set weechat.color.chat_nick_colors` shows the new list."],
      notes: [
        "WeeChat has no 24-bit color, and it needs a 256-color TERM (Ghostty and herdr provide one).",
        approx.length
          ? `${approx.length} color(s) had no exact ANSI match and use the nearest xterm-256 color: ${approx.join("; ")}.`
          : "Every color maps exactly onto a terminal ANSI color.",
      ],
    };
  },
};
